import React from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import ReadOnlyLocationBanner from '../components/ReadOnlyLocationBanner';
import WizardPreviewLayout from '../components/WizardPreviewLayout';
import WizardStepProfile from '../components/WizardStepProfile';
import { portalCopy } from '../copy';
import { usePartnerBusiness } from '../context/PartnerBusinessContext';
import { useWizardListingPreview } from '../hooks/useWizardListingPreview';
import { partnerBusinessPath, partnerPanelPath, partnerWizardPath } from '../partnerPaths';

const PartnerEditProfilePage: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const { business, isLoading, updateDraft } = usePartnerBusiness();
  const { preview, updatePreview } = useWizardListingPreview(business, 2);

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center font-jakarta text-[#5a3158]">
        Cargando…
      </div>
    );
  }

  if (!business) {
    return <Navigate to={partnerPanelPath()} replace />;
  }

  if (businessId && business.id !== businessId) {
    return <Navigate to={partnerPanelPath()} replace />;
  }

  if (business.status !== 'active') {
    return <Navigate to={partnerWizardPath()} replace />;
  }

  const businessPath = partnerBusinessPath(business.id);

  const goBack = () => {
    navigate(businessPath);
  };

  const handleSave: React.ComponentProps<typeof WizardStepProfile>['onSubmit'] = (patch) => {
    updateDraft(patch);
    navigate(businessPath, { state: { profileSaved: true } });
  };

  return (
    <div>
      <Link
        to={businessPath}
        className="inline-flex font-jakarta text-sm font-semibold text-chisme-primary hover:underline"
      >
        ← {portalCopy.business.edit.back}
      </Link>

      <h1 className="mt-4 font-instrument text-3xl italic tracking-[-0.01em] text-[#2a0f2a]">
        {portalCopy.business.edit.title}
      </h1>
      <p className="mt-2 font-jakarta text-[15px] leading-relaxed text-[#5a3158]">
        {portalCopy.business.edit.subtitle}
      </p>

      <div className="mt-8">
        <WizardPreviewLayout preview={preview}>
          <div className="space-y-6">
            <ReadOnlyLocationBanner business={business} />
            <WizardStepProfile
              mode="edit"
              business={business}
              onPreviewChange={updatePreview}
              onBack={goBack}
              onSubmit={handleSave}
            />
          </div>
        </WizardPreviewLayout>
      </div>
    </div>
  );
};

export default PartnerEditProfilePage;
